import { Injectable } from '@angular/core';
import { AbstractControl, NgForm, ValidatorFn, Validators } from '@angular/forms';
import { CHECKOUT_LABEL, SHIPPING_LABEL } from 'src/constants/constants';
import {
  EMAIL_PATTERN,
  TEXT_PATTERN,
  MIXED_PATTERN,
  USA_ZIP_PATTERN,
  CANADIAN_POSTAL_PATTERN,
  NUMBER_PATTERN,
  PHONE_PATTERN,
  ADDRESS_PATTERN,
  COUNTRIES
} from 'src/constants/validation-patterns';

/**
 * Service that provides shared form validation logic and utilities
 * for account and shipping forms
 */
@Injectable({
  providedIn: 'root'
})
export class FormValidationService {

  // Validation patterns exposed for template use
  readonly emailPattern: string = EMAIL_PATTERN;
  readonly textPattern: string = TEXT_PATTERN;
  readonly mixedPattern: string = MIXED_PATTERN;
  readonly usaZipPattern: string = USA_ZIP_PATTERN;
  readonly canadianPostalPattern: string = CANADIAN_POSTAL_PATTERN;
  readonly numberPattern: string = NUMBER_PATTERN;
  readonly phonePattern: string = PHONE_PATTERN;
  readonly addressPattern: string = ADDRESS_PATTERN;

  // Constants
  readonly errorImage: string = 'assets/img/icon-warning-pointer.gif';
  readonly hideStateByCountryList: Array<string> = ['United Kingdom', 'Virgin Islands, British'];

  constructor() { }

  /**
   * Helper method to set validators for a form field
   * @param form - The NgForm instance
   * @param fieldName - The field name
   * @param validators - Array of validators to apply
   */
  setFieldValidators(form: NgForm | null, fieldName: string, validators: ValidatorFn[]): void {
    if (!form?.controls) {
      return;
    }
    const control = form.controls[fieldName];
    if (control) {
      control.setValidators(validators);
      control.updateValueAndValidity();
    }
  }

  /**
   * Gets a form control by name
   * @param form - The NgForm instance
   * @param fieldName - The field name
   * @returns The AbstractControl or null
   */
  getFormControl(form: NgForm | null, fieldName: string): AbstractControl | null {
    return form?.controls?.[fieldName] || null;
  }

  /**
   * Validates postal/zip code based on country
   * - USA: ZIP format (91320 or 91320-1234)
   * - Canada: Postal code format (A1A 1A1)
   * - Other countries: General alphanumeric pattern
   * @param country - The country name
   * @param defaultCountry - Default country if country is not provided
   * @returns ValidatorFn that validates postal code format
   */
  validatePostalCode(country: string | undefined, defaultCountry: string = COUNTRIES.UNITED_STATES): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null; // Let required validator handle empty values
      }

      const currentCountry = country || defaultCountry || COUNTRIES.UNITED_STATES;
      const value = String(control.value).trim();

      // Map country to pattern and error key
      const countryPatternMap: { [key: string]: { pattern: string, errorKey: string } } = {
        [COUNTRIES.UNITED_STATES]: {
          pattern: this.usaZipPattern,
          errorKey: 'invalidZipCode'
        },
        [COUNTRIES.CANADA]: {
          pattern: this.canadianPostalPattern,
          errorKey: 'invalidPostalCode'
        }
      };

      // Get pattern for country or use default mixed pattern
      const countryConfig = countryPatternMap[currentCountry] || {
        pattern: this.mixedPattern,
        errorKey: 'invalidPostalCode'
      };

      const regex = new RegExp(countryConfig.pattern);
      const errorKey = 'pattern'; // Use standard pattern error for HTML compatibility

      if (!regex.test(value)) {
        return {
          [errorKey]: { value: control.value, country: currentCountry },
          [countryConfig.errorKey]: { value: control.value, country: currentCountry } // Keep custom error for debugging
        };
      }

      return null;
    };
  }

  /**
   * Returns the appropriate pattern for postal/zip code based on country
   * Used in HTML template for client-side validation
   * @param country - The country name
   * @param defaultCountry - Default country if country is not provided
   * @returns The pattern string for the postal code
   */
  getPostalCodePattern(country: string | undefined, defaultCountry: string = COUNTRIES.UNITED_STATES): string {
    const currentCountry = country || defaultCountry || COUNTRIES.UNITED_STATES;

    if (currentCountry === COUNTRIES.UNITED_STATES) {
      return this.usaZipPattern;
    }

    if (currentCountry === COUNTRIES.CANADA) {
      return this.canadianPostalPattern;
    }

    return this.mixedPattern;
  }

  /**
   * Handles blur event on postal code field to trigger validation
   * @param form - The NgForm instance
   * @param fieldName - The postal code field name
   */
  onPostalCodeBlur(form: NgForm | null, fieldName: string): void {
    const control = this.getFormControl(form, fieldName);
    if (control) {
      control.updateValueAndValidity();
      control.markAsTouched();
    }
  }

  /**
   * Revalidates postal code when address/country changes
   * @param form - The NgForm instance
   * @param fieldName - The postal code field name
   * @param country - The country name
   * @param defaultCountry - Default country if country is not provided
   */
  revalidatePostalCode(
    form: NgForm | null,
    fieldName: string,
    country: string | undefined,
    defaultCountry: string = COUNTRIES.UNITED_STATES
  ): void {
    const control = this.getFormControl(form, fieldName);
    if (control) {
      // Reapply validators to ensure country-based validation is current
      control.setValidators([
        Validators.required,
        this.validatePostalCode(country, defaultCountry)
      ]);
      control.updateValueAndValidity();
      control.markAsTouched(); // Mark as touched to show errors
    }
  }

  /**
   * Gets the localized label for a form field from CHECKOUT_LABEL
   * @param label - The label key to retrieve
   * @param country - The country name for localization
   * @param defaultCountry - Default country if country is not provided
   * @returns The localized label string
   */
  getCheckoutLabel(label: string, country: string | undefined, defaultCountry: string = COUNTRIES.UNITED_STATES): string {
    try {
      const currentCountry = country || defaultCountry;
      if (currentCountry &&
        typeof CHECKOUT_LABEL[currentCountry] === 'object' &&
        typeof CHECKOUT_LABEL[currentCountry][label] === 'string') {
        return CHECKOUT_LABEL[currentCountry][label];
      }
    } catch (e) {
      console.warn('Error getting checkout label for country:', country, e);
    }
    return CHECKOUT_LABEL[defaultCountry]?.[label] || label;
  }

  /**
   * Gets the localized label for a form field from SHIPPING_LABEL
   * @param label - The label key to retrieve
   * @param country - The country name for localization
   * @param defaultCountry - Default country if country is not provided
   * @returns The localized label string
   */
  getShippingLabel(label: string, country: string | undefined, defaultCountry: string = COUNTRIES.UNITED_STATES): string {
    try {
      const currentCountry = country || defaultCountry;
      if (currentCountry &&
        typeof SHIPPING_LABEL[currentCountry] === 'object' &&
        typeof SHIPPING_LABEL[currentCountry][label] === 'string') {
        return SHIPPING_LABEL[currentCountry][label];
      }
    } catch (e) {
      console.warn('Error getting shipping label for country:', country, e);
    }
    return SHIPPING_LABEL[defaultCountry]?.[label] || label;
  }

  /**
   * Checks if the app is running in Facebook's in-app browser
   * @returns true if running in Facebook app, false otherwise
   */
  isFacebookApp(): boolean {
    const win = window as any;

    if (win.facebookAutofillDisabled) {
      return false; // FB auto fill disabled (e.g., when user clicks PayPal button)
    }

    if (win.facebookAutofillComplete) {
      return false; // FB auto fill already completed
    }

    const userAgent = navigator.userAgent || navigator.vendor || win.opera || '';
    return userAgent.indexOf("FBAN") > -1 || userAgent.indexOf("FBAV") > -1;
  }

  /**
   * Display the state field if it is not included in the hideStateByCountryList
   * @param country - The country name
   * @returns true if state field should be displayed
   */
  displayStateField(country: string | undefined): boolean {
    return !this.hideStateByCountryList.includes(country || '');
  }

  /**
   * Creates validators for name fields (first name, last name)
   * @returns Array of ValidatorFn for name fields
   */
  getNameValidators(): ValidatorFn[] {
    return [Validators.required, Validators.pattern(this.textPattern)];
  }

  /**
   * Creates validators for street/address fields
   * @returns Array of ValidatorFn for address fields
   */
  getAddressValidators(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.pattern(this.addressPattern)
    ];
  }

  /**
   * Creates validators for city fields
   * @returns Array of ValidatorFn for city fields
   */
  getCityValidators(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.pattern(this.textPattern)
    ];
  }

  /**
   * Creates validators for postal/zip code fields
   * @param country - The country name
   * @param defaultCountry - Default country if country is not provided
   * @returns Array of ValidatorFn for postal code fields
   */
  getPostalCodeValidators(country: string | undefined, defaultCountry: string = COUNTRIES.UNITED_STATES): ValidatorFn[] {
    return [
      Validators.required,
      this.validatePostalCode(country, defaultCountry)
    ];
  }

  /**
   * Creates validators for phone fields (optional)
   * @returns Array of ValidatorFn for phone fields
   */
  getPhoneValidators(): ValidatorFn[] {
    return [Validators.pattern(this.phonePattern)];
  }

  /**
   * Creates validators for email fields
   * @param scope - Component scope reference (for email confirmation validation)
   * @param emailFieldName - Name of the email field
   * @param confirmEmailFieldName - Name of the confirm email field
   * @param firstValidationRef - Reference to firstValidation flag for synchronization
   * @returns Array of ValidatorFn for email fields
   */
  getEmailValidators(
    scope: any,
    emailFieldName: string,
    confirmEmailFieldName: string,
    firstValidationRef: { value: boolean }
  ): ValidatorFn[] {
    return [
      Validators.required,
      Validators.email,
      Validators.pattern(this.emailPattern),
      this.isValidEmail(),
      this.isConfirmEmail(scope, emailFieldName, confirmEmailFieldName, firstValidationRef)
    ];
  }

  /**
   * Validator for email format validation
   * @returns ValidatorFn that validates email format
   */
  private isValidEmail(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null; // Let required validator handle empty values
      }

      // Validate email pattern and provide custom error key for HTML template
      const pattern = this.emailPattern.replace(/^\^/, '').replace(/\$$/, '');
      const regex = new RegExp('^' + pattern + '$');

      const isValid = regex.test(control.value);
      return isValid
        ? null
        : { 'invalidValidEmail': { value: control.value } };
    };
  }

  /**
   * Validator to ensure email and confirm email fields match
   * @param scope - Component scope reference
   * @param emailFieldName - Name of the email field
   * @param confirmEmailFieldName - Name of the confirm email field
   * @param firstValidationRef - Reference to firstValidation flag for synchronization
   * @returns ValidatorFn that validates email confirmation
   */
  private isConfirmEmail(
    scope: any,
    emailFieldName: string,
    confirmEmailFieldName: string,
    firstValidationRef: { value: boolean }
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      // Get the parent form group
      const parent = control.parent;
      if (!parent) {
        return null;
      }

      // Get both email fields from the form controls
      const controls = parent.controls as { [key: string]: AbstractControl };

      const emailControl = controls[emailFieldName];
      const confirmEmailControl = controls[confirmEmailFieldName];

      if (!emailControl || !confirmEmailControl) {
        return null;
      }

      // Get values from form controls (not from component properties)
      const email = emailControl.value;
      const confirmEmail = confirmEmailControl.value;

      // If either field is empty, don't validate yet (let required validator handle it)
      if (!email || !confirmEmail) {
        return null;
      }

      // Synchronize validation between email and confirm email fields
      if (firstValidationRef.value) {
        firstValidationRef.value = false;

        if (emailControl === control) {
          confirmEmailControl.updateValueAndValidity();
        } else if (confirmEmailControl === control) {
          emailControl.updateValueAndValidity();
        }

        firstValidationRef.value = true;
      }

      // Validate that emails match (case-insensitive)
      const emailsMatch = email.toLowerCase() === confirmEmail.toLowerCase();

      return emailsMatch
        ? null
        : { 'invalidConfirmEmails': { value: control.value } };
    };
  }

  /**
   * Triggers validation on both email fields to keep them synchronized
   * @param form - The NgForm instance
   * @param emailFieldName - Name of the email field
   * @param confirmEmailFieldName - Name of the confirm email field
   */
  validateEmailFields(form: NgForm | null, emailFieldName: string, confirmEmailFieldName: string): void {
    if (form && form.controls) {
      const emailControl = form.controls[emailFieldName];
      const confirmEmailControl = form.controls[confirmEmailFieldName];

      if (emailControl) {
        emailControl.updateValueAndValidity();
      }
      if (confirmEmailControl) {
        confirmEmailControl.updateValueAndValidity();
      }
    }
  }
}

